import { Link } from "react-router-dom";
import { useUiLang } from "../../../stores/uiLang";

function IncidentResponsePageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Incident Response Policy</h1>
        <p className="text-lg max-w-2xl mx-auto">
          How Project Explore detects, responds to, and notifies stakeholders
          of security incidents affecting student data.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Our Commitment</h2>
          <p>
            Startup Smartup is committed to protecting the personal information
            of all Project Explore users, with particular care given to student
            data. When security incidents occur, we respond quickly, communicate
            transparently, and work to prevent recurrence.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">2. Incident Detection</h2>
          <p className="mb-3">
            Project Explore monitors platform activity continuously for signs
            of security incidents, including:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Authentication anomalies (unusual login patterns, credential-based attacks)</li>
            <li>Anomalous data access patterns</li>
            <li>Vendor security advisories from our infrastructure providers</li>
            <li>Reports from teachers, administrators, students, and parents</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">3. Incident Classification and Response</h2>
          <p className="mb-3">
            We classify security incidents by severity and respond accordingly.
            Our response process includes:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Containment:</strong> Isolating the affected system, revoking compromised credentials, or disabling affected functionality as quickly as possible</li>
            <li><strong>Investigation:</strong> Determining the scope of the incident, what data may have been affected, and how access was obtained</li>
            <li><strong>Remediation:</strong> Fixing the underlying vulnerability and verifying the fix</li>
            <li><strong>Post-incident review:</strong> A written review of every significant incident within 30 days of resolution</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Breach Notification</h2>
          <p className="mb-3">
            If we determine that a security incident has resulted in
            unauthorized access to student personal information, we commit to
            notifying affected contracting districts within 72 hours of making
            that determination.
          </p>
          <p className="mb-3">
            A breach notification to a District will include: the scope and
            timing of the incident, the categories of personal information
            affected, remediation steps taken, recommendations for affected
            individuals, and a designated point of contact for follow-up
            questions.
          </p>
          <p className="mb-3">
            Notification to parents of affected students will be coordinated
            with the contracting district in accordance with FERPA, COPPA, and
            applicable state law.
          </p>
          <p>
            Where required by applicable law, we will notify relevant regulatory
            authorities.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">5. Coordination with Districts</h2>
          <p className="mb-2">
            We work closely with contracting districts throughout any security
            incident affecting their students. We will:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Notify the district's designated administrator contact within the timeframes described above</li>
            <li>Provide a clear description of what occurred and what data was affected</li>
            <li>Provide a post-incident report within 30 days of incident closure</li>
            <li>Cooperate with any district investigation of the incident</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Annual Tabletop Exercise</h2>
          <p>
            Startup Smartup conducts an annual simulated incident exercise to
            test and improve our incident response processes. This exercise is
            designed to ensure our team is prepared to respond effectively to
            realistic threat scenarios.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">7. Reporting a Security Concern</h2>
          <p className="mb-3">
            If you suspect a security vulnerability or incident involving
            Project Explore, please report it to us immediately:
          </p>
          <p className="mb-3">
            <strong>Email:</strong>{" "}
            <a href="mailto:security@startupsmartup.com" className="text-indigo-600 underline">
              security@startupsmartup.com
            </a>
          </p>
          <p className="mb-3">
            Teachers and administrators reporting suspected issues should also
            notify their school or district technology administrator.
          </p>
          <p className="mb-4">
            We investigate all credible reports. We do not pursue legal action
            against security researchers who report issues in good faith through
            this channel.
          </p>
          <p>
            <strong>Responsible disclosure.</strong> Security researchers
            identifying potential vulnerabilities are invited to report
            responsibly via{" "}
            <a href="mailto:security@startupsmartup.com" className="text-indigo-600 underline">
              security@startupsmartup.com
            </a>
            . Project Explore commits to acknowledging reports within 5 business
            days and to coordinating remediation timelines with reporters.
            Project Explore does not currently maintain a bug bounty program.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Contact</h2>
          <p>
            <strong>Justin Ghiglia</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026
          <br />
          Cross-reference:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>{" | "}
          <Link to="/privacy#coppa" className="text-indigo-600 underline">COPPA Addendum</Link>{" | "}
          <Link to="/ferpa" className="text-indigo-600 underline">FERPA Statement</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">SOPIPA Acknowledgment</Link>
        </div>
      </section>
    </div>
  );
}

function IncidentResponsePageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Política de respuesta a incidentes</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Cómo Project Explore detecta los incidentes de seguridad que afectan
          los datos de los estudiantes, cómo responde y cómo avisa a las
          partes interesadas.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Nuestro compromiso</h2>
          <p>
            Startup Smartup se compromete a proteger la información personal de
            todos los usuarios de Project Explore, con un cuidado especial por
            los datos de los estudiantes. Cuando ocurren incidentes de
            seguridad, respondemos rápido, nos comunicamos con transparencia y
            trabajamos para que no se repitan.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">2. Detección de incidentes</h2>
          <p className="mb-3">
            Project Explore vigila la actividad de la plataforma de forma
            continua para detectar señales de incidentes de seguridad, entre
            ellas:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Anomalías de autenticación (patrones de inicio de sesión inusuales, ataques basados en credenciales)</li>
            <li>Patrones anómalos de acceso a los datos</li>
            <li>Avisos de seguridad de los proveedores de nuestra infraestructura</li>
            <li>Reportes de maestros, administradores, estudiantes y padres</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">3. Clasificación de incidentes y respuesta</h2>
          <p className="mb-3">
            Clasificamos los incidentes de seguridad por su gravedad y
            respondemos en consecuencia. Nuestro proceso de respuesta incluye:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Contención:</strong> Aislar el sistema afectado, revocar las credenciales comprometidas o desactivar la función afectada lo más rápido posible</li>
            <li><strong>Investigación:</strong> Determinar el alcance del incidente, qué datos pudieron verse afectados y cómo se obtuvo el acceso</li>
            <li><strong>Corrección:</strong> Arreglar la vulnerabilidad de fondo y verificar la corrección</li>
            <li><strong>Revisión posterior al incidente:</strong> Una revisión por escrito de cada incidente importante en un plazo de 30 días después de resolverlo</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Notificación de filtraciones</h2>
          <p className="mb-3">
            Si determinamos que un incidente de seguridad resultó en acceso no
            autorizado a información personal de estudiantes, nos
            comprometemos a avisar a los distritos contratantes afectados en un
            plazo de 72 horas después de hacer esa determinación.
          </p>
          <p className="mb-3">
            La notificación de una filtración a un Distrito incluirá: el
            alcance y el momento del incidente, las categorías de información
            personal afectadas, los pasos de corrección que se tomaron, las
            recomendaciones para las personas afectadas y un punto de contacto
            designado para preguntas de seguimiento.
          </p>
          <p className="mb-3">
            La notificación a los padres de los estudiantes afectados se
            coordinará con el distrito contratante de acuerdo con FERPA, COPPA
            y la ley estatal aplicable.
          </p>
          <p>
            Cuando la ley aplicable lo exija, avisaremos a las autoridades
            reguladoras correspondientes.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">5. Coordinación con los distritos</h2>
          <p className="mb-2">
            Trabajamos de cerca con los distritos contratantes durante todo
            incidente de seguridad que afecte a sus estudiantes.
            Haremos lo siguiente:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Avisar al contacto administrador designado del distrito dentro de los plazos descritos arriba</li>
            <li>Dar una descripción clara de lo que ocurrió y de qué datos se vieron afectados</li>
            <li>Entregar un informe posterior al incidente en un plazo de 30 días después de cerrarlo</li>
            <li>Cooperar con cualquier investigación del distrito sobre el incidente</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Simulacro anual</h2>
          <p>
            Startup Smartup realiza cada año un ejercicio de incidente
            simulado para probar y mejorar nuestros procesos de respuesta a
            incidentes. Este ejercicio busca asegurar que nuestro equipo esté
            preparado para responder con eficacia a escenarios de amenazas
            realistas.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">7. Cómo reportar un problema de seguridad</h2>
          <p className="mb-3">
            Si sospechas que hay una vulnerabilidad o un incidente de seguridad
            relacionado con Project Explore, repórtalo de inmediato:
          </p>
          <p className="mb-3">
            <strong>Correo electrónico:</strong>{" "}
            <a href="mailto:security@startupsmartup.com" className="text-indigo-600 underline">
              security@startupsmartup.com
            </a>
          </p>
          <p className="mb-3">
            Los maestros y administradores que reporten posibles problemas
            también deben avisar al administrador de tecnología de su escuela o
            distrito.
          </p>
          <p className="mb-4">
            Investigamos todos los reportes creíbles. No emprendemos acciones
            legales contra los investigadores de seguridad que reportan
            problemas de buena fe por este medio.
          </p>
          <p>
            <strong>Divulgación responsable.</strong> Invitamos a los
            investigadores de seguridad que identifiquen posibles
            vulnerabilidades a reportarlas de forma responsable en{" "}
            <a href="mailto:security@startupsmartup.com" className="text-indigo-600 underline">
              security@startupsmartup.com
            </a>
            . Project Explore se compromete a acusar recibo de los reportes en
            un plazo de 5 días hábiles y a coordinar con quienes reportan los
            plazos de corrección. Por ahora, Project Explore no tiene un
            programa de recompensas por errores.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Contacto</h2>
          <p>
            <strong>Justin Ghiglia</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026
          <br />
          Referencias cruzadas:{" "}
          <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>{" | "}
          <Link to="/privacy#coppa" className="text-indigo-600 underline">Anexo de COPPA</Link>{" | "}
          <Link to="/ferpa" className="text-indigo-600 underline">Declaración de FERPA</Link>{" | "}
          <Link to="/legal/sopipa" className="text-indigo-600 underline">Reconocimiento de SOPIPA</Link>
        </div>
      </section>
    </div>
  );
}

export default function IncidentResponsePage() {
  return useUiLang() === "es" ? <IncidentResponsePageEs /> : <IncidentResponsePageEn />;
}
